import React, { type FC, useEffect } from 'react';
import { type CreativeTypes } from '@bynder-studio/render-core';
import { Button, Dropdown } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useRenditionProfiles from 'packages/hooks/useRenditionProfiles';
import { type RenditionConfiguration } from '~/services/RenditionsService';

type RenditionProfilesProps = {
    creativeType: CreativeTypes;
    onLoadRenditions: (creativeType: CreativeTypes) => void;
    onSelectProfile: (creativeType: CreativeTypes, profile: RenditionConfiguration) => void;
};

const RenditionProfiles: FC<RenditionProfilesProps> = ({ creativeType, onLoadRenditions, onSelectProfile }) => {
    const { translate } = useTranslate();
    const { renditionProfiles, selectedProfile, isLoadingRenditions, setSelectedProfile } = useRenditionProfiles({
        creativeType,
    });

    useEffect(() => {
        if (!isLoadingRenditions) {
            onLoadRenditions(creativeType);
        }
    }, [isLoadingRenditions, creativeType, onLoadRenditions]);

    useEffect(() => {
        if (selectedProfile) {
            onSelectProfile(creativeType, selectedProfile);
        }
    }, [selectedProfile, creativeType, onSelectProfile]);

    return (
        <Dropdown
            trigger={({ isOpen, ...triggerProps }) => (
                <Button
                    isPressed={isOpen}
                    isDisabled={isLoadingRenditions}
                    rightIcon={<Dropdown.Arrow />}
                    {...triggerProps}
                    isFullWidth
                >
                    <Dropdown.SelectButtonText>
                        {isLoadingRenditions ? translate('modal.quick-export.loading') : selectedProfile?.displayName}
                    </Dropdown.SelectButtonText>
                </Button>
            )}
            position="bottom"
        >
            {renditionProfiles.map((profile) => (
                <Dropdown.Item
                    key={profile.id}
                    onClick={() => setSelectedProfile(profile)}
                    isChecked={profile.id === selectedProfile?.id}
                >
                    {profile.displayName}
                </Dropdown.Item>
            ))}
        </Dropdown>
    );
};

export default RenditionProfiles;
